import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useCompanyEnterpriseValues } from "../hooks/useCompanyEnterpriseValues";
import { getChartColors } from "../constants/chartColors";
import { formatNumber } from "../utils/formatNumber";
import {
  transformFinancialData,
  calculateQuarterlyGrowthMetrics,
  calculateAnnualGrowthMetrics,
} from "../utils/chartDataTransformers";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import GrowthMetricsDisplay from "./shared/GrowthMetricsDisplay";
import ChartTooltip from "./shared/ChartTooltip";

interface SharesOutstandingChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const SharesOutstandingChart: React.FC<SharesOutstandingChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode === "dark");

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const period = getApiPeriodParam(effectiveViewMode);
  const maxLimit = effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const { data, isLoading, error } = useCompanyEnterpriseValues(
    ticker,
    maxLimit,
    period
  );

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return safeSlice(
      transformFinancialData(
        data.filter((item) => item.numberOfShares !== null),
        effectiveViewMode,
        "numberOfShares",
        (item) => item.numberOfShares
      ),
      effectiveLimit
    );
  }, [data, effectiveViewMode, effectiveLimit]);

  const growthMetrics = useMemo(() => {
    if (chartData.length === 0) return [];
    return effectiveViewMode === "quarterly"
      ? calculateQuarterlyGrowthMetrics(chartData, "numberOfShares")
      : calculateAnnualGrowthMetrics(chartData, "numberOfShares");
  }, [chartData, effectiveViewMode]);

  return (
    <GenericFinancialBarChart
      title="Shares Outstanding"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      viewMode={effectiveViewMode}
      barKeys={["numberOfShares"]}
      colors={[colors.positive]}
      yAxisLegend="Shares Outstanding"
      yAxisFormat={(value) => formatNumber(Math.abs(value) * 1000000, 1)}
      tooltipContent={({ indexValue, value, color }) => (
        <ChartTooltip
          header={String(indexValue)}
          headerColor={color}
          rows={[{ label: "Shares Outstanding", value: formatNumber(Math.abs(value as number) * 1000000, 2) }]}
        />
      )}
      growthMetrics={
        <GrowthMetricsDisplay metrics={growthMetrics} />
      }
    />
  );
};

export default SharesOutstandingChart;
